// Shared helpers for static-content routes (legal, FAQ, about).
import { getHost } from "@/lib/host";
import {
  canonicalOriginFromHost,
  localeFromHost,
  type LocaleKey,
} from "@/lib/locale-from-host";
import { buildHreflangLinks } from "@/lib/hreflang";

export type StaticSEO = Record<LocaleKey, { title: string; description: string }>;

export function staticLoader() {
  const host = getHost();
  return {
    locale: localeFromHost(host),
    origin: canonicalOriginFromHost(host),
  };
}

export function buildStaticHead(
  seoMap: StaticSEO,
  path: string,
  loaderData: { locale: LocaleKey; origin: string } | undefined,
  extra?: { ogType?: string; jsonLd?: (url: string, seo: { title: string; description: string }) => Record<string, unknown> },
) {
  const origin = loaderData?.origin ?? "https://www.foradarota.com";
  const locale = loaderData?.locale ?? "es";
  const seo = seoMap[locale];
  const url = `${origin}${path}`;
  return {
    meta: [
      { title: seo.title },
      { name: "description", content: seo.description },
      { property: "og:title", content: seo.title },
      { property: "og:description", content: seo.description },
      { property: "og:type", content: extra?.ogType ?? "website" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: seo.title },
      { name: "twitter:description", content: seo.description },
    ],
    // Fase 4 · P4-B #7 — hreflang em todas as rotas estáticas indexáveis.
    links: [
      { rel: "canonical", href: url },
      ...buildHreflangLinks(path),
    ],
    ...(extra?.jsonLd
      ? {
          scripts: [
            {
              type: "application/ld+json",
              children: JSON.stringify(extra.jsonLd(url, seo)),
            },
          ],
        }
      : {}),
  };
}

